import { Box, HStack, Skeleton, SkeletonCircle, SkeletonText, VisuallyHidden, VStack } from '@chakra-ui/react';
import { LOOP_RESTING_OPACITY, LOOP_TEXT_COLUMN_MAX_WIDTH } from '@/constants/contentFeed';
import LoopStage from '@/modules/content/loop/parts/LoopStage';
import MediaSkeleton from '@/modules/content/loop/parts/MediaSkeleton';
import PortraitFrame from '@/modules/content/loop/parts/PortraitFrame';
import { TContentType } from '@/types/Content.types';

const SKELETON_COLORS = { startColor: 'background.cloudGray', endColor: 'background.darkGray' } as const;

const RAIL_BUTTON_COUNT = 3;
const NAV_BUTTON_COUNT = 2;

// Placeholder for the attribution column: creator row, title, description, meta row —
// the same blocks TextColumn renders, in the same order and at the same sizes.
const TextColumnSkeleton = () => (
  <VStack
    align="stretch"
    gap="10px"
    width="100%"
    maxWidth={{ base: '100%', md: LOOP_TEXT_COLUMN_MAX_WIDTH }}
    // The real column rests dimmed, so the skeleton does too — otherwise the chrome
    // visibly darkens at the moment content arrives.
    opacity={LOOP_RESTING_OPACITY}
  >
    <HStack width="100%" justify="space-between" align="flex-start" gap="10px">
      <HStack gap="10px" align="center">
        <SkeletonCircle size="34px" {...SKELETON_COLORS} />
        <VStack align="flex-start" gap="5px">
          <Skeleton height="11px" width="130px" {...SKELETON_COLORS} />
          <Skeleton height="9px" width="90px" {...SKELETON_COLORS} />
        </VStack>
      </HStack>
      <Skeleton height="11px" width="34px" {...SKELETON_COLORS} />
    </HStack>

    <SkeletonText noOfLines={2} spacing="8px" skeletonHeight="15px" {...SKELETON_COLORS} />
    <SkeletonText noOfLines={3} spacing="6px" skeletonHeight="9px" {...SKELETON_COLORS} />

    <HStack width="100%" justify="space-between" align="center">
      <Skeleton height="9px" width="70px" {...SKELETON_COLORS} />
      <HStack gap="6px">
        <Skeleton boxSize="26px" borderRadius="8px" {...SKELETON_COLORS} />
        <Skeleton boxSize="26px" borderRadius="8px" {...SKELETON_COLORS} />
      </HStack>
    </HStack>
  </VStack>
);

const CircleStack = ({ count, size, gap }: { count: number; size: string; gap: string }) => (
  <VStack gap={gap} flexShrink={0} opacity={LOOP_RESTING_OPACITY}>
    {Array.from({ length: count }, (_, index) => (
      <SkeletonCircle key={index} size={size} {...SKELETON_COLORS} />
    ))}
  </VStack>
);

// Loading state for the loop, shaped like the loop rather than a centred spinner: the
// media frame, the attribution column and both control stacks land in their final
// positions, so nothing jumps when the real content replaces this.
//
// `shape` is best-effort. The anchor's type is known only if that item is already in the
// query cache from the feed; otherwise this defaults to the reel layout, which shares its
// frame dimensions with every other variant — so the media block is correct either way and
// only the presence of the text column can differ.
const LoopSkeleton = ({ shape = 'video' }: { shape?: TContentType }) => (
  // A skeleton communicates "loading" visually and to nobody else, so the state is also
  // announced. The placeholder blocks themselves are hidden from assistive tech — they
  // carry no information, and reading them out would be noise.
  <Box role="status" aria-live="polite" aria-busy="true" width="100%">
    <VisuallyHidden>Loading content</VisuallyHidden>

    <Box aria-hidden="true">
      <LoopStage
        left={shape === 'post' ? undefined : <TextColumnSkeleton />}
        center={
          <PortraitFrame>
            <MediaSkeleton />
          </PortraitFrame>
        }
        right={
          <>
            <CircleStack count={RAIL_BUTTON_COUNT} size="42px" gap="14px" />
            <CircleStack count={NAV_BUTTON_COUNT} size="38px" gap="12px" />
          </>
        }
      />
    </Box>
  </Box>
);

export default LoopSkeleton;
